import { useEffect, useRef } from "react";
import { Outlet, useLocation, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { AuthProvider as PerlabAuthProvider } from "@perlab/auth-bridge";
import {
  AppShellLayout,
  SidebarProvider,
  ToastProvider,
  RightInspectorProvider,
} from "@perlab/ui";
import { ContextChainProvider, deriveScopeScalars, useContextChain } from "./state/ContextChain";
import { BusProvider } from "./state/BusProvider";
import { useThemeAccountSync } from "./state/useAccountTheme";
import { FlagsProvider } from "./flags/FlagsContext";
import { dispatchEngagementChanged, nextEpoch } from "./state/Engagement";
import { TopNav } from "./components/TopNav";
import { SideNav } from "./components/SideNav";
import { BottomTabBar } from "./components/BottomTabBar";
import { Footer } from "./components/Footer";
import { ShellBanners } from "./components/ShellBanners";
import { GleanReleaseBanner } from "./glean/GleanReleaseBanner";
import { OpenAppsStrip } from "./components/OpenAppsStrip";
import { canUseSplitView, SplitPane } from "./components/SplitPane";
import { AppContentPanel } from "./components/AppContentPanel";
import { useShellStore } from "./state/shellStore";
import { useMaxEsc } from "./state/useMaxEsc";
import { useOpenApps } from "./state/openApps";
import { ShellCommandPalette } from "./command-palette/ShellCommandPalette";
import { RightSurface, RightEdgeTab } from "./inspector/RightSurface";
import { PinsProvider } from "./state/usePins";
import { useSessionIdentity } from "../lib/auth/useSessionIdentity";
import { LastProjectBridge } from "./state/lastProject";
import { AccessRequestPage } from "../pages/AccessRequestPage";
import {
  EntitlementsProvider,
  SANDBOX_ACCESS_PREFIX,
  WORKSPACE_ACCESS_KEY,
} from "../lib/auth/EntitlementsContext";

function ProductAccessDenied({ product }: { product: "Workspace" | "SandBox" }): React.ReactElement {
  return (
    <main className="grid min-h-screen place-items-center bg-[var(--color-surface-base)] p-6 text-[var(--color-text-primary)]">
      <section className="w-full max-w-lg rounded-[var(--radius-container,8px)] border border-[var(--color-border-subtle)] bg-[var(--color-surface-elevated)] p-8 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[var(--color-accent)]">Access required</p>
        <h1 className="mt-2 text-2xl font-semibold">{product} is not assigned to your account</h1>
        <p className="mt-3 text-sm leading-6 text-[var(--color-text-secondary)]">Return to the product chooser to open an assigned product, or contact your PERL administrator for access.</p>
        <a href="/workspace/" className="mt-6 inline-flex h-10 items-center rounded-[var(--radius-control,4px)] bg-[var(--color-accent)] px-4 text-sm font-semibold text-white hover:bg-[var(--color-accent-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] focus-visible:ring-offset-2">Return to product chooser</a>
      </section>
    </main>
  );
}

/**
 * Hydrate ContextChain scope from the typed route params + broadcast engagement
 * changes. Depends ONLY on the param values + the STABLE syncScope (useCallback)
 * — never on the whole chain value, whose identity changes on every scope
 * mutation (that would re-run forever). Phase 0.4 (TanStack-native ContextChain).
 */
function useUrlScopeSync(): void {
  const params = useParams({ strict: false }) as {
    ws?: string;
    client?: string;
    eng?: string;
    project?: string;
    app?: string;
    node?: string;
  };
  const { syncScope, syncTrail, scopeNodes } = useContextChain();
  const prevEngRef = useRef<string | null>(null);

  const { ws, client, eng, project, app, node } = params;

  useEffect(() => {
    if (!ws) return;
    // Generic /w/$ws/n/$node route (§6): the typed scalars are absent, so DERIVE client/engagement/
    // project from the node's ANCESTRY (parent-walk) — free-shape safe, so the breadcrumb + tree
    // reflect a free-placed node's true position. Typed routes pass the scalars directly (unchanged).
    let dClient = client;
    let dEng = eng;
    let dProject = project;
    if (node) {
      ({ client: dClient, eng: dEng, project: dProject } = deriveScopeScalars(node, scopeNodes));
    }
    syncScope({ ws, client: dClient, eng: dEng, project: dProject, app: app ?? null });
    // Persistent trail: the current node is the URL's $node (generic route) or the deepest typed
    // scalar (all are node ids). syncTrail keeps a deeper trail — AND the open app — when you click an
    // ancestor crumb, so the app crumb stays re-enterable (drops on a new branch).
    syncTrail(node || project || eng || client || ws, app ?? "");

    if (dEng) {
      if (prevEngRef.current !== null && prevEngRef.current !== dEng) {
        dispatchEngagementChanged({
          rg: ws,
          eng: dEng,
          prevEng: prevEngRef.current,
          epoch: nextEpoch(),
        });
      }
      prevEngRef.current = dEng;
    }
  }, [ws, client, eng, project, app, node, syncScope, syncTrail, scopeNodes]);
}

/** Side-effect-only host — must run inside ContextChainProvider + the router. */
function ScopeSyncBridge(): null {
  useUrlScopeSync();
  return null;
}

/** Side-effect-only host — seeds the device theme cookie from the account (cross-device),
 *  once the profile query resolves. Must run inside BusProvider (useTheme) with the query client. */
function ThemeAccountSyncBridge(): null {
  useThemeAccountSync();
  return null;
}

/**
 * AppShell — pathless layout route component. Wraps every authenticated scoped
 * route with the shell chrome (TopNav breadcrumb + SideNav) via @perlab/ui's
 * AppShellLayout, plus the ContextChain + cross-tab Bus providers.
 *
 * Session-presence is enforced at the component level (matching the existing
 * Login/Home pattern); the beforeLoad gate + role/tier authz is Phase 0.5.
 * Inspector / right-rail and the deferred toolbar are out of this wave's cut.
 */
export function AppShell(): React.ReactElement | null {
  // Session-presence is enforced by the route's beforeLoad gate (Phase 0.5) —
  // this component only renders for an authenticated session.
  // 6.5 de-mock: resolve the REAL identity+roles from /auth/session (single source) and feed it to
  // the @perlab/auth-bridge provider. Render NOTHING until it hydrates so no component ever reads a
  // default/admin user during the async fetch (R-H1 fail-open window). Fail-closed: user=null on any
  // error → the bridge reports signed-out (hasRole → false).
  const { user, session, hydrated } = useSessionIdentity();
  const { pathname } = useLocation();

  // App focus/Max (CFR-VW-145): hide chrome (header + sidebar) for a near-full-screen app. Gated on
  // an app actually being active, so you can never end up chromeless with nothing to exit to. Esc
  // exits (redesign). Hooks run unconditionally (above the hydrated early-return) per rules-of-hooks.
  const appMax = useShellStore((s) => s.appMax);
  const hasActiveApp = useOpenApps((s) => s.activeKey !== null);
  const maxed = appMax && hasActiveApp;
  // Split view is meaningful only with a primary app active (same-scope secondary beside it).
  // Source of truth = the URL (`?split=<app>`), so it survives refresh / is shareable.
  const { split: splitAppId } = useSearch({ strict: false }) as { split?: string };
  const { app: primaryAppId } = useParams({ strict: false }) as { app?: string };
  const navigate = useNavigate();
  const splitAllowed = canUseSplitView(primaryAppId, splitAppId);
  const splitActive = hasActiveApp && splitAllowed;
  useEffect(() => {
    if (!splitAppId || splitAllowed) return;
    navigate({
      search: (previous: Record<string, unknown>) => ({
        ...previous,
        split: undefined,
      }),
    } as never);
  }, [navigate, splitAllowed, splitAppId]);
  useMaxEsc(maxed);

  if (!hydrated) return null;
  if (session && (session.access_status ?? "ACTIVE") !== "ACTIVE") return <AccessRequestPage session={session} />;
  const appAccess = session?.app_access ?? [];
  const hasAllProductAccess = appAccess.includes("*");
  const hasWorkspaceAccess = hasAllProductAccess || appAccess.includes(WORKSPACE_ACCESS_KEY);
  const hasSandboxAccess = hasAllProductAccess
    || hasWorkspaceAccess
    || appAccess.some((key) => key.startsWith(SANDBOX_ACCESS_PREFIX));
  const isProductLanding = pathname === "/";
  const isSandboxRoute = /^\/w\/[^/]+\/sandbox\/?$/.test(pathname);
  const isMissionControlRoute = /^\/w\/[^/]+\/admin\/mission-control\/?$/.test(pathname);
  const canAccessMissionControlWithoutWorkspace = isMissionControlRoute
    && (session?.roles ?? []).some((role) => role === "Admin" || role === "SuperAdmin");

  if (isProductLanding) {
    return (
      <PerlabAuthProvider user={user}>
        <EntitlementsProvider appAccess={appAccess} tabAccess={session?.tab_access ?? []}>
          <GleanReleaseBanner actorId={user?.userId ?? null} tenant={user?.tenant} />
          <Outlet />
        </EntitlementsProvider>
      </PerlabAuthProvider>
    );
  }
  if (isSandboxRoute && !hasSandboxAccess) return <ProductAccessDenied product="SandBox" />;
  if (!isSandboxRoute && !hasWorkspaceAccess && !canAccessMissionControlWithoutWorkspace) {
    return <ProductAccessDenied product="Workspace" />;
  }
  return (
    <PerlabAuthProvider user={user}>
    <EntitlementsProvider appAccess={appAccess} tabAccess={session?.tab_access ?? []}>
      <ContextChainProvider>
        <ScopeSyncBridge />
        <LastProjectBridge />
        <BusProvider>
          <ThemeAccountSyncBridge />
          <FlagsProvider>
            <SidebarProvider>
              <ToastProvider>
                <RightInspectorProvider>
                  <PinsProvider>
                    <AppShellLayout
                      topNav={<TopNav />}
                      sideNav={maxed ? undefined : <SideNav />}
                      hideChrome={maxed}
                      rightSlot={<RightSurface />}
                      // Edge tab deliberately SURVIVES Max: the inspector is Max-compatible
                      // (rightSlot stays mounted), so the hidden-inspector reveal must remain
                      // reachable — hiding it would strand the inspector, not fix a leak.
                      rightEdgeTab={<RightEdgeTab />}
                      // Max chrome-leak fix (INSTR-007 §3.3, RENDERED-proven 2026-07-19): footer
                      // and mobile tab bar are shell chrome and now hide with the rest of it.
                      bottomTabBar={maxed ? undefined : <BottomTabBar />}
                      footer={maxed ? undefined : <Footer />}
                    >
                      {/* Shell body = a flex COLUMN filling <main>'s height: fixed-height app rail +
                          banners on top, content region takes the rest (flex-1 min-h-0). This gives the
                          split panes their full height (T4) — the split row now stretches instead of
                          collapsing to content height. Non-split keeps a single flex-1 content box. */}
                      <div className="flex h-full min-h-0 flex-col">
                        {/* Embedded-apps runtime (CFR-VW-145): keep the open-apps strip first so its
                            rail stays aligned with the sibling right-surface header. */}
                        <OpenAppsStrip />
                        {/* Rows 4.5/4.6: condition banners sit below the shared app rail and above
                            content; they render null in normal operation. */}
                        <ShellBanners />
                        <GleanReleaseBanner actorId={user?.userId ?? null} tenant={user?.tenant} />
                        {/* Split view (session-only, same-scope): the primary (route) app + a second
                            guarded pane side by side, both full-height. NON-split renders a single
                            flex-1 content box. GuardedApp enforces the same mount guards in both panes. */}
                        {/* RD-7: `shell-app-panel` is the STABLE shell-owned id for the primary
                            content region — the open-apps strip's tabs reference it via
                            aria-controls (activating a tab swaps what renders here). Exactly one
                            instance exists per render (either branch), so the id never duplicates. */}
                        {splitActive ? (
                          <div className="flex min-h-0 min-w-0 flex-1" data-app="split-view">
                            <AppContentPanel id="shell-app-panel" className="border-r border-[var(--color-border-subtle)]">
                              <Outlet />
                            </AppContentPanel>
                            <SplitPane />
                          </div>
                        ) : (
                          <AppContentPanel id="shell-app-panel">
                            <Outlet />
                          </AppContentPanel>
                        )}
                        <ShellCommandPalette />
                      </div>
                    </AppShellLayout>
                  </PinsProvider>
                </RightInspectorProvider>
              </ToastProvider>
            </SidebarProvider>
          </FlagsProvider>
        </BusProvider>
      </ContextChainProvider>
    </EntitlementsProvider>
    </PerlabAuthProvider>
  );
}
